"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildAadManifest = buildAadManifest;
const tslib_1 = require("tslib");
const fs = tslib_1.__importStar(require("fs-extra"));
const path = tslib_1.__importStar(require("path"));
const isUUID_1 = tslib_1.__importDefault(require("validator/lib/isUUID"));
const localizeUtils_1 = require("../../../../common/localizeUtils");
const stringUtils_1 = require("../../../../common/stringUtils");
const common_1 = require("../../../../error/common");
const common_2 = require("../../../utils/common");
const invalidFieldInManifestError_1 = require("../error/invalidFieldInManifestError");
const constants_1 = require("../utility/constants");
const aadManifestHelper_1 = require("./aadManifestHelper");
const actionName = "aadApp/update"; // DO NOT MODIFY the name
const helpLink = "https://aka.ms/teamsfx-actions/aadapp-update";
async function buildAadManifest(context, manifestPath, outputFilePath, state) {
    const manifestAbsolutePath = getAbsolutePath(manifestPath, context.projectPath);
    if (!(await fs.pathExists(manifestAbsolutePath))) {
        throw new common_1.FileNotFoundError(actionName, manifestAbsolutePath, helpLink);
    }
    const manifest = await loadManifest(manifestAbsolutePath, state);
    const warningMessage = aadManifestHelper_1.AadManifestHelper.validateManifest(manifest);
    if (warningMessage) {
        warningMessage.split("\n").forEach((warning) => {
            context.logProvider?.warning(warning);
        });
    }
    if (!manifest.id || !(0, isUUID_1.default)(manifest.id)) {
        throw new invalidFieldInManifestError_1.MissingFieldInManifestUserError(actionName, "id", helpLink);
    }
    // Output actual manifest to project folder first for better troubleshooting experience
    const outputFileAbsolutePath = getAbsolutePath(outputFilePath, context.projectPath);
    await fs.ensureDir(path.dirname(outputFileAbsolutePath));
    await fs.writeFile(outputFileAbsolutePath, JSON.stringify(manifest, null, 4), "utf8");
    context.logProvider?.info((0, localizeUtils_1.getLocalizedString)(constants_1.logMessageKeys.outputAadAppManifest, outputFileAbsolutePath));
    return manifest;
}
function getAbsolutePath(relativeOrAbsolutePath, projectPath) {
    return path.isAbsolute(relativeOrAbsolutePath)
        ? relativeOrAbsolutePath
        : path.join(projectPath, relativeOrAbsolutePath);
}
async function loadManifest(manifestPath, state) {
    let generatedNewPermissionId = false;
    try {
        const manifestTemplate = await fs.readFile(manifestPath, "utf8");
        const permissionIdPlaceholderRegex = /\${{ *AAD_APP_ACCESS_AS_USER_PERMISSION_ID *}}/;
        // generate a new permission id if there's no one in env and manifest needs it
        if (!process.env.AAD_APP_ACCESS_AS_USER_PERMISSION_ID) {
            const matches = permissionIdPlaceholderRegex.exec(manifestTemplate);
            if (matches) {
                const permissionId = (0, stringUtils_1.getUuid)();
                process.env.AAD_APP_ACCESS_AS_USER_PERMISSION_ID = permissionId;
                if (state) {
                    state.AAD_APP_ACCESS_AS_USER_PERMISSION_ID = permissionId;
                }
                generatedNewPermissionId = true;
            }
        }
        const manifestString = (0, common_2.expandEnvironmentVariable)(manifestTemplate);
        const unresolvedEnvironmentVariable = (0, common_2.getEnvironmentVariables)(manifestString);
        if (unresolvedEnvironmentVariable && unresolvedEnvironmentVariable.length > 0) {
            const error = new common_1.MissingEnvironmentVariablesError("aadAppUpdate", unresolvedEnvironmentVariable.join(", "), manifestPath, helpLink);
            throw error;
        }
        let manifest;
        try {
            manifest = JSON.parse(manifestString);
        }
        catch (error) {
            // JSON.parse only throws SyntaxError per doc, which is a subsclass of Error
            throw new common_1.JSONSyntaxError(manifestPath, error, actionName);
        }
        aadManifestHelper_1.AadManifestHelper.processRequiredResourceAccessInManifest(manifest);
        return manifest;
    }
    finally {
        if (generatedNewPermissionId) {
            // restore environment variable to avoid impact to other code
            delete process.env.AAD_APP_ACCESS_AS_USER_PERMISSION_ID;
        }
    }
}
//# sourceMappingURL=buildAadManifest.js.map